import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle,
  Copy,
  Columns,
  Folder,
  House,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Rows,
  Star,
  WarningCircle,
  Wind,
  SquaresFour,
  Wrench,
} from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";
import { proposalSaveError } from "../../lib/proposalPresentationDraft";
import {
  DiscountEditor,
  LineEditor,
  ServiceField,
  Specifications,
} from "./ServiceEditor";
import {
  SettingsHelp,
  SettingsSaveBar,
  SettingsSection,
} from "./ProposalSettingsUI";
import {
  duplicateService,
  newLine,
  serviceKey,
  starterTypes,
  startingService,
  tradeNames,
  type ServiceConfiguration,
  type ServiceLine,
  type ServiceOption,
  type ServiceScope,
  type ServiceTemplate,
} from "../../shared/proposal-services";

const emptyConfiguration = (): ServiceConfiguration => ({
  version: 2,
  include_roofing: false,
  services: [],
});
const unitNames: Record<string, string> = {
  lf: "linear foot",
  sf: "square foot",
  sq: "square (100 sq ft)",
  ea: "each",
  item: "item",
  opening: "opening",
  run: "run",
  hour: "hour",
};
const money = (value: string) =>
  Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
const grid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  gap: 2,
};
const allLines = (value?: ServiceConfiguration | null) => [
  ...(value?.services.flatMap((service) =>
    service.options.flatMap((option) => option.lines),
  ) || []),
  ...(value?.project_charges || []),
];
const missingPrices = (value?: ServiceConfiguration | null) =>
  allLines(value).filter(
    (line) => line.unit_price === null || line.unit_price === "",
  ).length;
export function ServiceIcon({
  trade,
  size = 22,
}: {
  trade: string;
  size?: number;
}) {
  const Icon =
    {
      siding: House,
      windows: SquaresFour,
      gutters: Wind,
      fencing: Columns,
      repair: Wrench,
      combined: Rows,
    }[trade] || Rows;
  return <Icon size={size} />;
}
type Draft = {
  template: ServiceTemplate | null;
  name: string;
  folder: string;
  favorite: boolean;
  configuration: ServiceConfiguration;
};
type LineDraft = {
  serviceKey?: string;
  optionKey?: string;
  index: number;
  line: ServiceLine;
  lines: ServiceLine[];
};

export function ServiceTemplates({
  onLegacyDefaults,
  onDirtyChange,
}: {
  onLegacyDefaults?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const client = useQueryClient();
  const settings = useQuery({
    queryKey: ["proposal-service-settings"],
    queryFn: async () =>
      (await apiClient.get("/api/proposal-service-templates/settings/"))
        .data as { enabled: boolean; can_manage: boolean },
  });
  const library = useQuery({
    queryKey: ["proposal-service-templates"],
    queryFn: async () => {
      const data = (await apiClient.get("/api/proposal-service-templates/"))
        .data as ServiceTemplate[] | { results: ServiceTemplate[] };
      return Array.isArray(data) ? data : data.results;
    },
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState("");
  const [trade, setTrade] = useState("all");
  const [favorites, setFavorites] = useState(false);
  const [archived, setArchived] = useState(false);
  const [editorTab, setEditorTab] = useState("items");
  const [activeService, setActiveService] = useState("");
  const [starterOpen, setStarterOpen] = useState(false);
  const [starterIndex, setStarterIndex] = useState(0);
  const [newName, setNewName] = useState("");
  const [lineDraft, setLineDraft] = useState<LineDraft | null>(null);
  const [confirmation, setConfirmation] = useState<
    "leave" | "archive" | "remove-service" | null
  >(null);
  const [howOpen, setHowOpen] = useState(false);
  const canManage = settings.data?.can_manage === true;
  useEffect(() => {
    onDirtyChange?.(dirty || !!lineDraft);
  }, [dirty, lineDraft, onDirtyChange]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty || lineDraft) event.preventDefault();
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty, lineDraft]);
  const edit = (change: Partial<Draft>) => {
    setDraft((previous) => (previous ? { ...previous, ...change } : previous));
    setDirty(true);
    setMessage("");
  };
  const configure = (configuration: ServiceConfiguration) =>
    edit({ configuration });
  const service =
    draft?.configuration.services.find((row) => row.key === activeService) ||
    draft?.configuration.services[0];
  const changeService = (next: ServiceScope) => {
    if (draft)
      configure({
        ...draft.configuration,
        services: draft.configuration.services.map((row) =>
          row.key === next.key ? next : row,
        ),
      });
  };
  const changeOption = (next: ServiceOption) => {
    if (service)
      changeService({
        ...service,
        options: service.options.map((row) =>
          row.key === next.key ? next : row,
        ),
      });
  };
  const openTemplate = (template: ServiceTemplate) => {
    setDraft({
      template,
      name: template.name,
      folder: template.folder,
      favorite: template.is_favorite,
      configuration: structuredClone(
        template.configuration || emptyConfiguration(),
      ),
    });
    setDirty(false);
    setEditorTab("items");
    setActiveService(template.configuration?.services[0]?.key || "");
    setMessage("");
  };
  const leave = () => {
    setDraft(null);
    setDirty(false);
    setConfirmation(null);
    setLineDraft(null);
  };
  const invalidate = () =>
    client.invalidateQueries({ queryKey: ["proposal-service-templates"] });
  const save = async () => {
    if (!draft || !canManage || saving) return;
    if (!draft.name.trim()) {
      setMessage("Enter a template name before saving.");
      setEditorTab("details");
      return;
    }
    if (!draft.configuration.services.length) {
      setMessage("Add at least one service before saving.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      let template = draft.template;
      if (!template) {
        template = (
          await apiClient.post("/api/proposal-service-templates/", {
            name: draft.name.trim(),
            folder: draft.folder.trim(),
            is_favorite: draft.favorite,
          })
        ).data as ServiceTemplate;
        // Keep the created ID if publishing fails so retry never creates duplicates.
        setDraft((previous) =>
          previous ? { ...previous, template } : previous,
        );
      }
      await apiClient.patch(`/api/proposal-service-templates/${template.id}/`, {
        name: draft.name.trim(),
        folder: draft.folder.trim(),
        is_favorite: draft.favorite,
      });
      const published = (
        await apiClient.post(
          `/api/proposal-service-templates/${template.id}/publish/`,
          {
            configuration: draft.configuration,
            expected_version: template.current_version,
          },
        )
      ).data as ServiceTemplate;
      openTemplate(published);
      setMessage(
        "Template saved. Existing proposals keep their saved scope and prices.",
      );
      await invalidate();
    } catch (error) {
      setMessage(proposalSaveError(error));
    } finally {
      setSaving(false);
    }
  };
  const updateMetadata = async (
    template: ServiceTemplate,
    fields: Partial<ServiceTemplate>,
  ) => {
    if (!canManage || saving) return;
    setSaving(true);
    setMessage("");
    try {
      await apiClient.patch(
        `/api/proposal-service-templates/${template.id}/`,
        fields,
      );
      await invalidate();
      if (fields.is_archived !== undefined) leave();
    } catch (error) {
      setMessage(proposalSaveError(error));
    } finally {
      setSaving(false);
    }
  };
  const addStarter = () => {
    const starter = starterTypes[starterIndex];
    if (!draft && !newName.trim()) {
      setMessage("Enter a name for the new template.");
      return;
    }
    const next = startingService(starter.trade, starter.variant);
    if (draft)
      configure({
        ...draft.configuration,
        services: [...draft.configuration.services, next],
      });
    else {
      setDraft({
        template: null,
        name: newName.trim(),
        folder: "",
        favorite: false,
        configuration: { ...emptyConfiguration(), services: [next] },
      });
      setDirty(true);
    }
    setActiveService(next.key);
    setStarterOpen(false);
    setEditorTab("items");
    setMessage("");
  };
  const applyLine = () => {
    if (!draft || !lineDraft) return;
    if (!lineDraft.line.name.trim()) {
      setMessage("Enter an item name before applying your changes.");
      return;
    }
    const update = (lines: ServiceLine[]) =>
      lineDraft.index < 0
        ? [...lines, lineDraft.line]
        : lines.map((line, index) =>
            index === lineDraft.index ? lineDraft.line : line,
          );
    configure(
      lineDraft.serviceKey
        ? {
            ...draft.configuration,
            services: draft.configuration.services.map((row) =>
              row.key === lineDraft.serviceKey
                ? {
                    ...row,
                    options: row.options.map((option) =>
                      option.key === lineDraft.optionKey
                        ? { ...option, lines: update(option.lines) }
                        : option,
                    ),
                  }
                : row,
            ),
          }
        : {
            ...draft.configuration,
            project_charges: update(
              draft.configuration.project_charges || [],
            ).map((line) => ({ ...line, optional: false, is_fee: true })),
          },
    );
    setLineDraft(null);
  };
  const removeLine = () => {
    if (!draft || !lineDraft) return;
    if (lineDraft.index >= 0)
      configure(
        lineDraft.serviceKey
          ? {
              ...draft.configuration,
              services: draft.configuration.services.map((row) =>
                row.key === lineDraft.serviceKey
                  ? {
                      ...row,
                      options: row.options.map((option) =>
                        option.key === lineDraft.optionKey
                          ? {
                              ...option,
                              lines: option.lines.filter(
                                (_, index) => index !== lineDraft.index,
                              ),
                            }
                          : option,
                      ),
                    }
                  : row,
              ),
            }
          : {
              ...draft.configuration,
              project_charges: draft.configuration.project_charges?.filter(
                (_, index) => index !== lineDraft.index,
              ),
            },
      );
    setLineDraft(null);
  };
  const lineRows = (
    lines: ServiceLine[],
    owner?: ServiceScope,
    option?: ServiceOption,
  ) => (
    <Stack divider={<Divider />}>
      {lines.map((line, index) => (
        <Stack
          key={line.key}
          direction="row"
          gap={2}
          alignItems="center"
          sx={{ py: 2, minWidth: 0 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="title-sm" sx={{ overflowWrap: "anywhere" }}>
              {line.name}
            </Typography>
            <Typography
              level="body-xs"
              textColor={
                line.optional ? "primary.plainColor" : "text.secondary"
              }
            >
              {line.optional
                ? "Optional — customer chooses"
                : line.is_fee
                  ? "Required charge"
                  : "Included in the price"}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              level="title-sm"
              color={line.unit_price === null ? "warning" : undefined}
            >
              {line.unit_price === null ? "Add price" : money(line.unit_price)}
            </Typography>
            <Typography level="body-xs">
              per {unitNames[line.unit] || line.unit}
            </Typography>
          </Box>
          <IconButton
            aria-label={`Edit ${line.name}`}
            disabled={!canManage || saving || !!draft?.template?.is_archived}
            onClick={() =>
              setLineDraft({
                serviceKey: owner?.key,
                optionKey: option?.key,
                index,
                line: structuredClone(line),
                lines,
              })
            }
          >
            <PencilSimple size={19} />
          </IconButton>
        </Stack>
      ))}
      <Button
        variant="plain"
        startDecorator={<Plus size={18} />}
        sx={{ alignSelf: "start", mt: 1 }}
        disabled={!canManage || saving}
        onClick={() =>
          setLineDraft({
            serviceKey: owner?.key,
            optionKey: option?.key,
            index: -1,
            line: { ...newLine(), is_fee: !owner },
            lines,
          })
        }
      >
        Add item or fee
      </Button>
    </Stack>
  );

  if (settings.isPending || library.isPending)
    return (
      <Stack alignItems="center" gap={2} sx={{ py: 6 }}>
        <CircularProgress />
        <Typography role="status">Loading your service templates…</Typography>
      </Stack>
    );
  if (settings.isError || library.isError)
    return (
      <Alert
        color="danger"
        endDecorator={
          <Button
            onClick={() => {
              void settings.refetch();
              void library.refetch();
            }}
          >
            Retry
          </Button>
        }
      >
        We couldn’t load service templates. Your saved settings have not
        changed.
      </Alert>
    );
  const templates = (library.data || []).filter(
    (template) => !!template.is_archived === archived,
  );
  const matches = templates.filter((template) => {
    const services = template.configuration?.services || [];
    const combined =
      services.length > 1 || !!template.configuration?.include_roofing;
    return (
      (!folder || template.folder === folder) &&
      (!favorites || template.is_favorite) &&
      (trade === "all" ||
        (trade === "combined"
          ? combined
          : services.some((row) => row.trade === trade))) &&
      `${template.name} ${template.folder} ${services.map((row) => row.name).join(" ")}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  });
  return (
    <Stack spacing={2.5}>
      {message && (
        <Alert
          role="status"
          color={message.startsWith("Template saved") ? "success" : "warning"}
        >
          {message}
        </Alert>
      )}
      {!draft ? (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            gap={2}
            alignItems={{ sm: "center" }}
          >
            <Box>
              <Typography level="h3">Services & Templates</Typography>
              <Typography level="body-sm" textColor="text.secondary">
                Save your common work and prices once. Reuse them on any
                proposal, then adjust for the job.
              </Typography>
            </Box>
            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => setHowOpen(true)}
              >
                How it works
              </Button>
              <Button
                startDecorator={<Plus size={18} />}
                disabled={!canManage}
                onClick={() => {
                  setStarterIndex(0);
                  setNewName(starterTypes[0].variant);
                  setStarterOpen(true);
                }}
              >
                New template
              </Button>
            </Stack>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Input
              startDecorator={<MagnifyingGlass size={20} />}
              placeholder="Search your templates…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              slotProps={{ input: { "aria-label": "Search templates" } }}
              sx={{ flex: 1 }}
            />
            <Select
              aria-label="Template folder"
              value={folder}
              onChange={(_, value) => setFolder(value || "")}
              sx={{ minWidth: 190 }}
            >
              <Option value="">All folders</Option>
              {Array.from(
                new Set(
                  templates.map((template) => template.folder).filter(Boolean),
                ),
              )
                .sort()
                .map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
            </Select>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            {[
              ["all", "All services"],
              ...Object.entries(tradeNames),
              ["combined", "Combined"],
            ].map(([value, label]) => (
              <Button
                key={value}
                size="sm"
                color="neutral"
                variant={trade === value ? "soft" : "outlined"}
                aria-pressed={trade === value}
                startDecorator={
                  value !== "all" && <ServiceIcon trade={value} size={17} />
                }
                onClick={() => setTrade(value)}
              >
                {label}
              </Button>
            ))}
            <Button
              size="sm"
              color="neutral"
              variant={favorites ? "soft" : "outlined"}
              aria-pressed={favorites}
              startDecorator={<Star size={17} />}
              onClick={() => setFavorites(!favorites)}
            >
              Favorites
            </Button>
            <Typography level="body-xs" sx={{ ml: { sm: "auto" } }}>
              {matches.length} templates
            </Typography>
          </Stack>
          <Box
            sx={{
              ...grid,
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2,minmax(0,1fr))",
                xl: "repeat(3,minmax(0,1fr))",
              },
            }}
          >
            {matches.map((template) => {
              const config = template.configuration;
              const missing = missingPrices(config);
              const services = config?.services || [];
              return (
                <Card
                  key={template.id}
                  variant="outlined"
                  sx={{ p: 0, gap: 0, minWidth: 0, overflow: "hidden" }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ px: 2.5, pt: 2.5 }}
                  >
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: "md",
                        bgcolor: "background.level1",
                        display: "flex",
                      }}
                    >
                      <ServiceIcon
                        trade={
                          services.length > 1 || config?.include_roofing
                            ? "combined"
                            : services[0]?.trade || "combined"
                        }
                        size={25}
                      />
                    </Box>
                    <IconButton
                      aria-label={`${template.is_favorite ? "Remove" : "Add"} ${template.name} ${template.is_favorite ? "from" : "to"} favorites`}
                      disabled={!canManage || saving}
                      onClick={() =>
                        void updateMetadata(template, {
                          is_favorite: !template.is_favorite,
                        })
                      }
                      color={template.is_favorite ? "warning" : "neutral"}
                    >
                      <Star
                        weight={template.is_favorite ? "fill" : "regular"}
                        size={20}
                      />
                    </IconButton>
                  </Stack>
                  <Box sx={{ p: 2.5, flex: 1 }}>
                    <Typography
                      level="title-lg"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {template.name}
                    </Typography>
                    <Typography
                      level="body-sm"
                      textColor="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      {[
                        config?.include_roofing ? "Roofing packages" : "",
                        ...allLines(config)
                          .slice(0, 3)
                          .map((row) => row.name),
                      ]
                        .filter(Boolean)
                        .join(" · ") ||
                        "Add services to complete this template."}
                    </Typography>
                    <Stack
                      direction="row"
                      gap={1}
                      flexWrap="wrap"
                      alignItems="center"
                      sx={{ mt: 2.5 }}
                    >
                      <Chip
                        size="sm"
                        variant="soft"
                        color={
                          !allLines(config).length || missing
                            ? "warning"
                            : "success"
                        }
                        startDecorator={
                          missing || !allLines(config).length ? (
                            <WarningCircle size={14} />
                          ) : (
                            <CheckCircle size={14} />
                          )
                        }
                      >
                        {!allLines(config).length
                          ? "Add items"
                          : missing
                            ? `${missing} prices to add`
                            : "Prices set"}
                      </Chip>
                      <Typography level="body-xs">
                        {allLines(config).length} items
                      </Typography>
                    </Stack>
                  </Box>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    sx={{ py: 1.5, px: 2.5 }}
                  >
                    <Typography
                      level="body-xs"
                      startDecorator={<Folder size={15} />}
                    >
                      {template.folder || "Unfiled"}
                    </Typography>
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      endDecorator={<ArrowRight size={17} />}
                      onClick={() => openTemplate(template)}
                    >
                      {canManage ? "Edit template" : "View template"}
                    </Button>
                  </Stack>
                </Card>
              );
            })}
          </Box>
          {!matches.length && (
            <Card sx={{ p: 4, alignItems: "center", textAlign: "center" }}>
              <BookOpen size={32} />
              <Typography level="title-lg">
                {templates.length
                  ? "No matching templates"
                  : archived
                    ? "No archived templates"
                    : "Create your first service template"}
              </Typography>
              <Typography level="body-sm">
                {templates.length
                  ? "Try another search or clear your filters."
                  : "Choose a starter, add your own prices, and save it for your team."}
              </Typography>
              {templates.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearch("");
                    setTrade("all");
                    setFolder("");
                    setFavorites(false);
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Card>
          )}
          <SettingsSection
            title="Library settings & older defaults"
            description="Availability, archived templates and legacy project defaults"
          >
            <Stack spacing={2}>
              <Checkbox
                label="Enable project services for this organization"
                checked={!!settings.data?.enabled}
                disabled={!canManage || saving}
                onChange={async (event) => {
                  setSaving(true);
                  try {
                    await apiClient.patch(
                      "/api/proposal-service-templates/settings/",
                      { enabled: event.target.checked },
                    );
                    await settings.refetch();
                  } catch (error) {
                    setMessage(proposalSaveError(error));
                  } finally {
                    setSaving(false);
                  }
                }}
              />
              <Typography level="body-sm">
                Turning this off stops new service proposals. Previously sent
                proposals remain available.
              </Typography>
              <Checkbox
                label="Show archived templates"
                checked={archived}
                onChange={(event) => setArchived(event.target.checked)}
              />
              {onLegacyDefaults && (
                <Box>
                  <Button variant="plain" onClick={onLegacyDefaults}>
                    Legacy project defaults
                  </Button>
                  <Typography level="body-xs">
                    The older “Other Projects” defaults are kept for existing
                    proposal workflows. Use service templates for new siding,
                    windows, gutters and fencing work.
                  </Typography>
                </Box>
              )}
            </Stack>
          </SettingsSection>
          {!settings.data?.enabled && (
            <Alert color="warning">
              Project services are currently disabled. Enable them under Library
              settings when your team is ready.
            </Alert>
          )}
          {!canManage && (
            <Alert>
              Only Org Admins can change shared templates. You can review the
              saved scope and prices.
            </Alert>
          )}
        </>
      ) : (
        <>
          <Button
            variant="plain"
            color="neutral"
            startDecorator={<ArrowLeft size={18} />}
            sx={{ alignSelf: "start" }}
            onClick={() => (dirty ? setConfirmation("leave") : leave())}
          >
            All templates
          </Button>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={2}
            justifyContent="space-between"
          >
            <Box>
              <Typography level="h3">{draft.name || "New template"}</Typography>
              <Typography level="body-sm" textColor="text.secondary">
                Set the starting scope and prices. Your team confirms job
                quantities on each proposal.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<Copy size={18} />}
              disabled={!canManage || saving}
              onClick={() =>
                edit({ template: null, name: `${draft.name} copy` })
              }
            >
              Make a copy
            </Button>
          </Stack>
          {draft.template?.is_archived && (
            <Alert
              color="warning"
              endDecorator={
                <Button
                  disabled={!canManage || saving}
                  onClick={() =>
                    draft.template &&
                    void updateMetadata(draft.template, { is_archived: false })
                  }
                >
                  Restore template
                </Button>
              }
            >
              This template is archived and won’t appear when creating
              proposals.
            </Alert>
          )}
          <Tabs
            value={editorTab}
            onChange={(_, value) => setEditorTab(String(value))}
            sx={{ bgcolor: "transparent" }}
          >
            <TabList>
              <Tab value="items">Items & pricing</Tab>
              <Tab value="details">Details & terms</Tab>
              <Tab value="preview">Preview</Tab>
            </TabList>
          </Tabs>
          <Box
            component="fieldset"
            disabled={!canManage || saving || !!draft.template?.is_archived}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <Stack spacing={2.5}>
              {editorTab === "details" ? (
                <>
                  <Card>
                    <Typography level="title-lg">Template details</Typography>
                    <ServiceField
                      label="Template name (required)"
                      value={draft.name}
                      onChange={(name) => edit({ name })}
                    />
                    <ServiceField
                      label="Folder (optional)"
                      value={draft.folder}
                      onChange={(folder) => edit({ folder })}
                    />
                    <Checkbox
                      label="Add to favorites"
                      checked={draft.favorite}
                      onChange={(event) =>
                        edit({ favorite: event.target.checked })
                      }
                    />
                  </Card>
                  <Card>
                    <Typography level="title-lg">Proposal pages</Typography>
                    <Typography level="body-sm">
                      Choose the starting pages for proposals made from this template.
                      Your team can adjust them in each proposal’s Pages list.
                      Existing proposals are unchanged.
                    </Typography>
                    {([
                      ['title', 'Cover page', true],
                      ['introduction', 'Introduction', true],
                      ['inspection', 'Inspection photos and findings', draft.configuration.include_roofing],
                      ['measurements', 'Roof measurements', draft.configuration.include_roofing],
                      ['manufacturer', 'Roofing manufacturer pages', draft.configuration.include_roofing],
                    ] as const).map(([key, label, fallback]) => (
                      <Checkbox
                        key={key}
                        label={label}
                        checked={draft.configuration.page_defaults?.[key] ?? fallback}
                        onChange={(event) => configure({
                          ...draft.configuration,
                          page_defaults: {
                            ...draft.configuration.page_defaults,
                            [key]: event.target.checked,
                          },
                        })}
                      />
                    ))}
                    <Typography level="body-xs">
                      Photos, findings and manufacturer pages need relevant content.
                      For services without roofing, roof measurements and inspection photos
                      start off. Pricing, acceptance and terms remain included.
                    </Typography>
                  </Card>
                  {draft.configuration.services.map((row) => (
                    <Card key={row.key}>
                      <Typography level="title-lg">{row.name}</Typography>
                      <ServiceField
                        label="Service name"
                        value={row.name}
                        onChange={(name) => changeService({ ...row, name })}
                      />
                      <FormControl>
                        <FormLabel>Included work / exclusions</FormLabel>
                        <Textarea
                          minRows={3}
                          value={row.terms}
                          onChange={(event) =>
                            changeService({ ...row, terms: event.target.value })
                          }
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Warranty (optional)</FormLabel>
                        <Textarea
                          minRows={2}
                          value={row.warranty}
                          onChange={(event) =>
                            changeService({
                              ...row,
                              warranty: event.target.value,
                            })
                          }
                        />
                      </FormControl>
                    </Card>
                  ))}
                </>
              ) : editorTab === "preview" ? (
                <>
                  <Alert>
                    This is a template preview. Job quantities, selections,
                    taxes and final totals are confirmed on each proposal.
                  </Alert>
                  {draft.configuration.include_roofing && (
                    <Card>
                      <Typography level="title-lg">
                        Roofing packages included
                      </Typography>
                      <Typography>
                        Roofing packages are selected when creating the
                        proposal.
                      </Typography>
                    </Card>
                  )}
                  {draft.configuration.services.map((row) => (
                    <Card key={row.key}>
                      <Typography level="h4">{row.name}</Typography>
                      {row.optional && <Chip size="sm">Optional service</Chip>}
                      {row.options.map((option) => (
                        <Box key={option.key}>
                          <Typography level="title-md">
                            {option.name}
                          </Typography>
                          {Number(option.discount?.value) > 0 && (
                            <Typography level="body-sm">
                              Package discount:{" "}
                              {option.discount?.type === "percent"
                                ? `${option.discount.value}%`
                                : money(option.discount!.value)}
                            </Typography>
                          )}
                          {option.lines.map((line) => (
                            <Stack
                              direction="row"
                              gap={2}
                              justifyContent="space-between"
                              key={line.key}
                              sx={{ py: 1.5 }}
                            >
                              <Box>
                                <Typography>{line.name}</Typography>
                                <Typography level="body-xs">
                                  {line.optional
                                    ? "Optional upgrade"
                                    : "Included · required"}
                                </Typography>
                                <Typography level="body-sm">
                                  {line.description}
                                </Typography>
                              </Box>
                              <Typography sx={{ flexShrink: 0 }}>
                                {line.unit_price === null
                                  ? "Price needed"
                                  : money(line.unit_price)}{" "}
                                / {line.unit}
                              </Typography>
                            </Stack>
                          ))}
                        </Box>
                      ))}
                      <Typography level="body-sm">{row.terms}</Typography>
                      {row.warranty && (
                        <Typography level="body-sm">
                          Warranty: {row.warranty}
                        </Typography>
                      )}
                    </Card>
                  ))}
                  {!!draft.configuration.project_charges?.length && (
                    <Card>
                      <Typography level="title-lg">
                        Shared required charges
                      </Typography>
                      {draft.configuration.project_charges.map((line) => (
                        <Stack
                          key={line.key}
                          direction="row"
                          gap={2}
                          justifyContent="space-between"
                        >
                          <Typography>{line.name}</Typography>
                          <Typography>
                            {line.unit_price === null
                              ? "Price needed"
                              : money(line.unit_price)}{" "}
                            / {line.unit}
                          </Typography>
                        </Stack>
                      ))}
                    </Card>
                  )}
                  {Number(draft.configuration.discount?.value) > 0 && (
                    <Typography level="body-sm">
                      Project discount:{" "}
                      {draft.configuration.discount?.type === "percent"
                        ? `${draft.configuration.discount.value}%`
                        : money(draft.configuration.discount!.value)}{" "}
                      on eligible work.
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  {draft.configuration.services.length > 1 && (
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {draft.configuration.services.map((row) => (
                        <Button
                          key={row.key}
                          color="neutral"
                          variant={
                            service?.key === row.key ? "soft" : "outlined"
                          }
                          startDecorator={
                            <ServiceIcon trade={row.trade} size={18} />
                          }
                          onClick={() => setActiveService(row.key)}
                        >
                          {row.name}
                        </Button>
                      ))}
                    </Stack>
                  )}
                  {service?.options.map((option) => (
                    <Card key={option.key} sx={{ p: 2.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          level="title-lg"
                          startDecorator={<ServiceIcon trade={service.trade} />}
                        >
                          {service.options.length > 1
                            ? option.name
                            : service.name}
                        </Typography>
                        <SettingsHelp title="Template prices">
                          Save your installed rates here. Your team confirms
                          lengths, areas and counts for each job. Supplier
                          purchasing quantities are handled separately.
                        </SettingsHelp>
                      </Stack>
                      {lineRows(option.lines, service, option)}
                    </Card>
                  ))}
                  {service && (
                    <SettingsSection
                      title="Service options"
                      description="Profile, color, customer choices and alternative packages"
                    >
                      <Stack spacing={2}>
                        <Specifications
                          value={service.specifications}
                          onChange={(specifications) =>
                            changeService({ ...service, specifications })
                          }
                        />
                        <Checkbox
                          label="Let the customer decline this entire service"
                          checked={service.optional}
                          onChange={(event) =>
                            changeService({
                              ...service,
                              optional: event.target.checked,
                            })
                          }
                        />
                        <ServiceField
                          label="Alternative group (optional)"
                          value={service.exclusive_group || ""}
                          onChange={(exclusive_group) =>
                            changeService({ ...service, exclusive_group })
                          }
                          help="Use the same group for mutually exclusive work, such as repairing or replacing the same wall. Customers can select only one service in that group."
                        />
                        {service.options.map((option) => (
                          <Card key={option.key} variant="soft">
                            <ServiceField
                              label="Package name"
                              value={option.name}
                              onChange={(name) =>
                                changeOption({ ...option, name })
                              }
                            />
                            <DiscountEditor
                              label="Package discount"
                              value={option.discount}
                              onChange={(discount) =>
                                changeOption({ ...option, discount })
                              }
                            />
                            {service.options.length > 1 && (
                              <Button
                                variant="plain"
                                color="danger"
                                onClick={() =>
                                  changeService({
                                    ...service,
                                    options: service.options.filter(
                                      (row) => row.key !== option.key,
                                    ),
                                    selected_option:
                                      service.selected_option === option.key
                                        ? service.options.find(
                                            (row) => row.key !== option.key,
                                          )!.key
                                        : service.selected_option,
                                  })
                                }
                              >
                                Remove package
                              </Button>
                            )}
                          </Card>
                        ))}
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const option = structuredClone(service.options[0]);
                            option.key = serviceKey();
                            option.name = "Alternative package";
                            changeService({
                              ...service,
                              options: [...service.options, option],
                            });
                          }}
                        >
                          Add alternative package
                        </Button>
                        <Stack direction="row" gap={1}>
                          <Button
                            variant="plain"
                            onClick={() =>
                              configure({
                                ...draft.configuration,
                                services: [
                                  ...draft.configuration.services,
                                  duplicateService(service),
                                ],
                              })
                            }
                          >
                            Duplicate service
                          </Button>
                          <Button
                            variant="plain"
                            color="danger"
                            onClick={() => setConfirmation("remove-service")}
                          >
                            Remove service
                          </Button>
                        </Stack>
                      </Stack>
                    </SettingsSection>
                  )}
                  <SettingsSection
                    title="Shared fees & project discount"
                    description="Charges that apply once across the whole project"
                  >
                    <Stack spacing={2}>
                      <Typography level="body-sm">
                        Use shared fees for mobilization or access. They remain
                        required even if an optional service is removed.
                      </Typography>
                      {lineRows(draft.configuration.project_charges || [])}
                      <DiscountEditor
                        label="Project discount"
                        value={draft.configuration.discount}
                        onChange={(discount) =>
                          configure({ ...draft.configuration, discount })
                        }
                      />
                      <Typography level="body-xs">
                        Line discounts apply first, then package discounts, then
                        the project discount. Tax applies to discounted taxable
                        amounts.
                      </Typography>
                    </Stack>
                  </SettingsSection>
                  <Card variant="outlined">
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      gap={2}
                      justifyContent="space-between"
                      alignItems={{ sm: "center" }}
                    >
                      <Box>
                        <Typography level="title-md">
                          More than one service?
                        </Typography>
                        <Typography level="body-sm">
                          Combine work into one reusable template.
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startDecorator={<Plus size={18} />}
                        onClick={() => setStarterOpen(true)}
                      >
                        Add another service
                      </Button>
                    </Stack>
                    <Checkbox
                      label="Include roofing packages"
                      checked={draft.configuration.include_roofing}
                      onChange={(event) =>
                        configure({
                          ...draft.configuration,
                          include_roofing: event.target.checked,
                        })
                      }
                    />
                  </Card>
                </>
              )}
            </Stack>
          </Box>
          {missingPrices(draft.configuration) > 0 && (
            <Typography level="body-sm" color="warning">
              {missingPrices(draft.configuration)} items still need pricing. You
              can save this starting point; prices must be completed before
              sending a proposal.
            </Typography>
          )}
          <SettingsSaveBar
            dirty={dirty}
            saving={saving}
            onSave={() => void save()}
            label="Save template"
            disabled={!canManage || !!draft.template?.is_archived}
          >
            <Typography level="body-xs">
              Existing proposals keep their saved scope and prices.
            </Typography>
          </SettingsSaveBar>
          {draft.template && !draft.template.is_archived && (
            <Button
              variant="plain"
              color="danger"
              sx={{ alignSelf: "start" }}
              disabled={!canManage || saving}
              onClick={() => setConfirmation("archive")}
            >
              Archive template
            </Button>
          )}
        </>
      )}
      <Modal open={starterOpen} onClose={() => setStarterOpen(false)}>
        <ModalDialog
          sx={{
            width: "min(620px, calc(100vw - 32px))",
            maxHeight: "90dvh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4">
            {draft ? "Add another service" : "What work do you want to save?"}
          </Typography>
          <Typography level="body-sm">
            Choose a starting scope, then add your own prices.
          </Typography>
          <Box sx={grid}>
            {Object.entries(tradeNames).map(([key, label]) => (
              <Button
                key={key}
                variant={
                  starterTypes[starterIndex].trade === key ? "soft" : "outlined"
                }
                color="neutral"
                startDecorator={<ServiceIcon trade={key} />}
                onClick={() => {
                  const index = starterTypes.findIndex(
                    (row) => row.trade === key,
                  );
                  setStarterIndex(index);
                  setNewName(starterTypes[index].variant);
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
          <FormControl>
            <FormLabel>Starting scope</FormLabel>
            <Select
              aria-label="Starting scope"
              value={starterIndex}
              onChange={(_, index) => {
                if (index !== null) {
                  setStarterIndex(index);
                  setNewName(starterTypes[index].variant);
                }
              }}
            >
              {starterTypes.map(
                (row, index) =>
                  row.trade === starterTypes[starterIndex].trade && (
                    <Option key={index} value={index}>
                      {row.variant}
                    </Option>
                  ),
              )}
            </Select>
          </FormControl>
          {!draft && (
            <ServiceField
              label="Template name (required)"
              value={newName}
              onChange={setNewName}
            />
          )}
          <Typography level="body-sm">
            Starters include suggested work, not prices or legal terms. Your
            team controls both.
          </Typography>
          <Button
            disabled={!canManage || (!draft && !newName.trim())}
            onClick={addStarter}
          >
            {draft ? "Add service" : "Continue to items"}{" "}
            <ArrowRight size={18} />
          </Button>
        </ModalDialog>
      </Modal>
      <Drawer
        anchor="right"
        open={!!lineDraft}
        onClose={() => setLineDraft(null)}
        size="lg"
        slotProps={{
          content: {
            sx: { width: { xs: "100%", sm: 650 }, maxWidth: "100vw" },
          },
        }}
      >
        <ModalClose />
        <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography level="h4">
            {lineDraft?.index === -1 ? "Add item or fee" : "Edit item"}
          </Typography>
          <Typography level="body-sm">
            Set the default once. Adjust it on each proposal.
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", p: 2, minHeight: 0 }}>
          {lineDraft && (
            <LineEditor
              templateMode
              line={lineDraft.line}
              lines={lineDraft.lines}
              onChange={(line) =>
                setLineDraft({
                  ...lineDraft,
                  line: lineDraft.serviceKey
                    ? line
                    : { ...line, optional: false, is_fee: true },
                })
              }
              onDuplicate={() =>
                setLineDraft({
                  ...lineDraft,
                  index: -1,
                  line: {
                    ...structuredClone(lineDraft.line),
                    key: serviceKey(),
                    name: `${lineDraft.line.name} copy`,
                  },
                })
              }
              onRemove={removeLine}
            />
          )}
        </Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={1}
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            variant="plain"
            color="neutral"
            onClick={() => setLineDraft(null)}
          >
            Cancel
          </Button>
          <Button
            disabled={!canManage || !lineDraft?.line.name.trim()}
            onClick={applyLine}
          >
            Apply changes
          </Button>
        </Stack>
      </Drawer>
      <Modal open={!!confirmation} onClose={() => setConfirmation(null)}>
        <ModalDialog>
          <Typography level="h4">
            {confirmation === "leave"
              ? "Leave without saving?"
              : confirmation === "archive"
                ? "Archive this template?"
                : "Remove this service?"}
          </Typography>
          <Typography>
            {confirmation === "leave"
              ? "Your changes have not been saved."
              : confirmation === "archive"
                ? "Your team will no longer see it when creating proposals. Unsaved edits will be discarded. You can restore the saved template from archived templates. Existing proposals stay unchanged."
                : "This removes the service from this template draft. Existing proposals stay unchanged."}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button variant="plain" onClick={() => setConfirmation(null)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                if (confirmation === "leave") leave();
                else if (confirmation === "archive" && draft?.template)
                  void updateMetadata(draft.template, { is_archived: true });
                else if (
                  confirmation === "remove-service" &&
                  draft &&
                  service
                ) {
                  configure({
                    ...draft.configuration,
                    services: draft.configuration.services.filter(
                      (row) => row.key !== service.key,
                    ),
                  });
                  setConfirmation(null);
                }
              }}
            >
              {confirmation === "leave"
                ? "Discard changes"
                : confirmation === "archive"
                  ? "Archive template"
                  : "Remove service"}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
      <Modal open={howOpen} onClose={() => setHowOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">
            Set up once. Reuse on any proposal.
          </Typography>
          <Typography>1. Choose a service and starting scope.</Typography>
          <Typography>
            2. Add your standard work, installed prices and terms.
          </Typography>
          <Typography>3. Save it for your team.</Typography>
          <Typography level="body-sm">
            Your team adds the template to a proposal and confirms quantities
            for that job. Templates can combine services with each other and
            with roofing packages.
          </Typography>
          <Button onClick={() => setHowOpen(false)}>Got it</Button>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
